import { z } from 'zod';
import { apiQuery, createQueryKeys } from '@/shared/data';
import { billingApi, getCurrentTenant } from '@/shared/http';
const cost = z.number().nullish();
export const walletForBillingSchema = z.looseObject({
  id: z.string().optional(),
  walletCode: z.number(),
  enableAutoSubmitToDebtCollection: z.boolean().optional(),
  bankSlipCosts: z
    .looseObject({
      register: cost,
      cancellation: cost,
      discount: cost,
      changeDueDate: cost,
      settlement: cost,
      debtCollection: cost,
      debtCollectionCancellation: cost,
      debtCollectionSettlement: cost,
      debtCollectionSuccess: cost,
      pixRegister: cost,
      pixSettlement: cost,
      pixCancellation: cost,
      pixChange: cost,
    })
    .optional(),
});
export type WalletForBilling = z.output<typeof walletForBillingSchema>;
const page = z.looseObject({ data: z.array(walletForBillingSchema) });
/** Legacy walletContext.tsx:233-267: first match, walletsCode only; tenant belongs to cache key. */
export async function getWalletByCode(
  walletCode: number,
  signal?: AbortSignal,
): Promise<WalletForBilling | undefined> {
  const data = await billingApi.get<unknown>('/Wallet', {
    params: { walletsCode: walletCode },
    ...(signal ? { signal } : {}),
  });
  const parsed = page.safeParse(data);
  if (parsed.success) return parsed.data.data[0];
  if (import.meta.env.DEV) console.warn('Wallet billing detail outside expected contract');
  return (data as { data?: WalletForBilling[] }).data?.[0];
}
const keys = createQueryKeys('wallet');
export function walletByCodeQuery(walletCode: number) {
  return apiQuery(
    keys.custom('by-code', { walletCode, tenant: getCurrentTenant() }),
    async ({ signal }) => (await getWalletByCode(walletCode, signal)) ?? null,
    { enabled: !!walletCode },
  );
}
export type FileInstructionInput = { readonly file: File; readonly name: string };

const multipart = { headers: { 'Content-Type': 'multipart/form-data' } } as const;

/** Small cross-module port: legacy FileInstruction multipart contract, kept out of wallet.api. */
export async function validateFileInstruction(input: FileInstructionInput, signal?: AbortSignal) {
  return billingApi.post('/FileInstruction/Validate', { file: input.file, name: input.name }, {
    ...multipart,
    ...(signal ? { signal } : {}),
  });
}

export async function postFileInstruction(input: FileInstructionInput, signal?: AbortSignal) {
  return billingApi.post('/FileInstruction', { file: input.file, name: input.name }, {
    ...multipart,
    ...(signal ? { signal } : {}),
  });
}
